import { inject } from "@angular/core";
import {
  CanActivateFn,
  Router,
  UrlTree,
} from "@angular/router";

import { ApplicationFactory } from "../../factories/ApplicationFactory";
import { GenericErrorHandler } from "../../services/error-handlers.service";
import { ErrorPageCode } from "../../app.enums";

import {
  ActivityIndicatorService,
  PublicTicketService,
  ThemeService,
} from "../../../core-ui";
import { ApplicationService } from "../../../core-ui/services/application.service";
import { PARAM_TICKET_ID } from "../../../core-ui/core-ui.enums";
import { LocaleService } from "../../../app/services";

export const appCustomerGuard: CanActivateFn = async (
  route,
  state,
): Promise<boolean | UrlTree> => {
  const applicationService = inject(ApplicationService);
  const applicationFactory = inject(ApplicationFactory);
  const floatingService = inject(ActivityIndicatorService);
  const publicTicketService = inject(PublicTicketService);
  const errorHandler = inject(GenericErrorHandler);
  const router = inject(Router);
  const themeService = inject(ThemeService);
  const localeService = inject(LocaleService);

  const discoverApplicationId = async (): Promise<string> => {
    const ticketId = route.paramMap.get(PARAM_TICKET_ID);

    const ticket = await publicTicketService.getTicket(ticketId);

    if (!ticket.properties.application_id && !ticket.properties.applicationId) {
      throw new Error("no applicationId for ticket");
    }

    return ticket.properties.application_id || ticket.properties.applicationId;
  };

  if (!!applicationService.getActiveApplication()) {
    return true;
  }

  try {
    floatingService.loading(true, "", true); // "initializing application"
    const applicationId = await discoverApplicationId();
    const application =
      await applicationFactory.createCustomerApplication(applicationId);
    applicationService.setActiveApplication(application);
    themeService.parseApplicationOptions(application);
    localeService.parseApplicationOptions(application.getConfig());
    return true;
  } catch (error) {
    errorHandler.handleError(error);
    return router.createUrlTree(
      [
        "/error",
        error?.status == 404
          ? ErrorPageCode.TICKET_NOT_FOUND
          : ErrorPageCode.APPLICATION_NOT_FOUND,
      ],
      {
        queryParams: {
          message:
            error?.status == 404
              ? undefined
              : "Wrong or malformed application id",
        },
      },
    );
  } finally {
    floatingService.loading(false);
  }
};
